import { useState, useEffect } from 'react';
import { Auth, Hub } from 'aws-amplify';
import { v4 } from 'uuid';
import { APIGetUser, APICreateUser, APIUpdateLastLoginTime, APIGetMyCompanies} from '../api/user/User';
import { APICreateCompany, APIGetCompanyData } from '../api/companies/Companies';
import {GetProductSettingsHandler} from "../helper/getProductSettingsHandler";

export const useUser = () => {

  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    const createNewCompany = async (cognitoUser, companyId = null) => {
      const newCompanyId = companyId ? companyId : v4();
      const updatedAttributes = {
        'custom:companyId': newCompanyId,
        'custom:role': 'admin',
      };
      try {
        await Promise.all([
          Auth.updateUserAttributes(cognitoUser, updatedAttributes),
          APICreateCompany(newCompanyId, cognitoUser.attributes['custom:type'], cognitoUser.attributes.email),
        ]);
        return { ...cognitoUser, attributes: { ...cognitoUser.attributes, ...updatedAttributes } };
      } catch (error) {
        console.error("Error creating new company:", error);
        throw error;
      }
    };
  
    const createNewDBUser = async (cognitoUser) => {
      try {
        if (!cognitoUser.attributes['custom:companyId']) {
          const updatedCognitoUser = await createNewCompany(cognitoUser);
          const newDBUser = await APICreateUser(updatedCognitoUser);
          return newDBUser;
        }
        const updatedCognitoUser = await createNewCompany(cognitoUser, cognitoUser.attributes['custom:companyId']);
        const newDBUser = await APICreateUser(updatedCognitoUser);
        return newDBUser;
      } catch (error) {
        console.error("Error creating new database user:", error);
        throw error;
      }
     
    };

    const getUser = async () => {
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        if (cognitoUser) {
          const dbUser = await APIGetUser(cognitoUser.attributes.sub);
          if (!dbUser) {
            const newDBUser = await createNewDBUser(cognitoUser);
            return newDBUser;
          }
          dbUser.cognitoUser = cognitoUser;
          return dbUser;
        }
        return null;
      } catch (error) {
        console.error("Error fetching authenticated user:", error);
        return null;
      }
    };

    const initializeUser = async () => {
      const dbUser = await getUser();
      if (!dbUser) {
        return;
      }
      const company = await APIGetCompanyData(dbUser.companyId);
      company["permissions"] = GetProductSettingsHandler(company?.["products"]);

      const myCompanies = await APIGetMyCompanies(dbUser.id);
      dbUser["myCompanies"] = myCompanies;

      setUser(dbUser);
      setCompany(company);
      APIUpdateLastLoginTime(dbUser.id);
    };

    initializeUser();

    const authListener = (data) => {
      const { payload } = data;
      if (payload.event === 'signIn') {
        initializeUser();
      } else if (payload.event === 'signOut') {
        setUser(null);
      }
    };

    const hubListener = Hub.listen('auth', authListener);

    return () => {
        hubListener();
    };
  }, [user?.id]);

  return { user, company};
};